import { TMGrayLogoIcon } from '@/icons/TMGrayLogoIcon'
import { cn } from '@/lib/utils'

import { Skeleton } from './skeleton'
import { Caption1, Caption2 } from './typography'

interface EmptyStateSkeletonProps {
  title?: string
  description?: string
  className?: string
}

export function EmptyStateSkeleton({
  title = 'No data',
  description,
  className,
}: EmptyStateSkeletonProps) {
  const rows = [0, 1, 2]

  return (
    <div
      className={cn(
        'relative flex h-full w-full flex-col gap-6 rounded-xl bg-white/60 p-3',
        className
      )}
    >
      <div className='flex w-full flex-col gap-6'>
        {rows.map((rowIndex) => (
          <div
            key={rowIndex}
            className={cn(
              'flex flex-wrap items-center justify-between gap-4 rounded-xl bg-slate-100 px-8 py-3',
              rowIndex === 0 && 'opacity-50',
              rowIndex === 1 && 'opacity-30',
              rowIndex === 2 && 'opacity-20'
            )}
          >
            {[0, 1].map((index) => (
              <div
                key={`big-${index}`}
                className={cn(
                  'flex items-center gap-3 flex-shrink-0',
                  index === 1 && 'hidden md:flex'
                )}
              >
                <Skeleton className='h-8 w-8 flex-shrink-0 rounded-lg bg-gray-300' />
                <Skeleton className='h-2.5 w-9 flex-shrink-0 rounded-lg bg-gray-300 md:w-20' />
              </div>
            ))}

            {rows.map((index) => (
              <div
                key={`small-${index}`}
                className={cn(
                  'flex items-center gap-3 flex-shrink-0',
                  index === 2 && 'hidden md:flex'
                )}
              >
                <Skeleton className='h-2.5 w-9 flex-shrink-0 rounded-lg bg-gray-300' />
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className='pointer-events-none absolute inset-0 flex items-center justify-center'>
        <div className='flex flex-col items-center gap-2 text-center'>
          <TMGrayLogoIcon width={20} height={18} className='text-gray-400' />

          {title && (
            <Caption1 semibold className='text-gray-800'>
              {title}
            </Caption1>
          )}

          {description && <Caption2 className='text-gray-500'>{description}</Caption2>}
        </div>
      </div>
    </div>
  )
}
